<template>
    <common-modal :width="'medium'" @emitEvent="emitEvent($event, ['close'])" @close="emitEvent({ name: 'close' })">
        <common-content-spacer height="small">
            <header>
                <h4>{{ modalHeading }}</h4>
            </header>
            <hr />

            <main>
                <common-content-spacer class="contact_details" height="large">
                    <p>{{ appointmentTime }} : {{ displayAppointmentDateTime }}</p>
                    <referral-details-card
                        :referral="referral"
                        :showReferralDate="false"
                        :showReferred="false"
                        :showReversed="true"
                        :showBottomText="false"
                        :showEmail="true"
                        widgetsSize="medium"
                    ></referral-details-card>
                    <div v-if="shouldShowTimeframeSelector">
                        <div class="timeslot-tabs">
                            <tabs-field
                                :label="$t('forms.referrals.search.fields.search_by_time_window.label')"
                                :options="searchTimeSlotOptions"
                                :formData="data"
                                valuePath="searchByTimeWindow"
                                :size="isMobileLarge ? 'extra-small' : 'small'"
                                @change="onTimeframeChange"
                            ></tabs-field>
                        </div>
                        <div v-if="showCustomCalendar" class="calendar-area">
                            <date-range-field
                                :formData="data"
                                :label="$t('pages.profile.professional.availability.pick_date')"
                                openSide="right"
                                :isSingle="true"
                                iconCustomClass="availability-schedule-form-icon"
                                valuePath="customDate"
                                :minDate="minSelectableDate"
                                @change="change($event)"
                            ></date-range-field>
                        </div>
                    </div>
                </common-content-spacer>
            </main>
            <hr />

            <footer>
                <h5>{{ agreeText }}</h5>
                <common-buttons>
                    <common-button
                        :label="cancelButtonLabel"
                        type="secondary"
                        size="large"
                        @click="followUpReferral ? emitEvent({ name: 'close' }) : emitEvent({ name: 'deny', payload: {} })"
                    ></common-button>
                    <common-button
                        :label="confirmButtonLabel"
                        type="primary"
                        size="large"
                        @click="
                            followUpReferral
                                ? emitEvent({ name: 'reschedule', payload: data })
                                : emitEvent({ name: 'reschedule', payload: {} })
                        "
                    ></common-button>
                </common-buttons>
            </footer>
        </common-content-spacer>
    </common-modal>
</template>

<script>
    import dayjs from 'dayjs'
    import ReferralDetailsCard from '../../page-elements/referrals/details/ReferralDetailsCard'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import FormMixin from 'mixins/FormMixin'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [ReferralStatusMixin, FormMixin, ScreenSizeDetectionMixin],
        components: {
            ReferralDetailsCard,
        },

        props: {
            referral: { type: Object, required: true },
            followUpReferral: { type: Boolean, required: true },
        },

        data() {
            return {
                showCustomCalendar: false,
                data: {
                    searchByTimeWindow: 'asap',
                    customDate: {
                        startDate: dayjs().startOf('day').toDate(),
                        endDate: null,
                    },
                },
            }
        },

        computed: {
            shouldShowTimeframeSelector() {
                return this.followUpReferral && this.referral?.toUser?.id !== this.$auth.getUser().id
            },
            minSelectableDate() {
                return dayjs().startOf('day').toDate()
            },
            searchTimeSlotOptions() {
                return [
                    ...TimeWindowSearchCriteriaEnum,
                    {
                        label: 'Custom',
                        value: 'custom',
                    },
                ]
            },
            displayAppointmentDateTime() {
                return dayjs(this.referral.appointmentDateTime).format('MMMM DD, HH:mm A')
            },
            modalType() {
                return !this.followUpReferral && this.isReferralConfirmed(this.referral) ? 'cancel' : 'decline'
            },
            modalHeading() {
                return this.followUpReferral
                    ? this.$t('pages.referrals.show.reschedule_referral_new_modal.heading')
                    : this.$t('pages.referrals.show.reschedule_modal.heading')
            },
            appointmentTime() {
                return this.followUpReferral
                    ? this.$t('pages.referrals.show.reschedule_referral_new_modal.previous_appointment_time')
                    : this.$t('pages.referrals.show.reschedule_modal.orig_appointment_time')
            },
            cancelButtonLabel() {
                return this.followUpReferral
                    ? this.$t('pages.referrals.show.reschedule_referral_new_modal.buttons.cancel')
                    : this.$t(`pages.referrals.show.reschedule_modal.buttons.${this.modalType}_referral`)
            },
            confirmButtonLabel() {
                return this.followUpReferral
                    ? this.$t('pages.referrals.show.reschedule_referral_new_modal.buttons.create_appointment')
                    : this.$t('pages.referrals.show.reschedule_modal.buttons.update_schedule')
            },
            agreeText() {
                return this.followUpReferral
                    ? this.$t('pages.referrals.show.reschedule_referral_new_modal.agree_create_new_appointment')
                    : this.$t('pages.referrals.show.reschedule_modal.agreed_new_appoinment')
            },
        },
        methods: {
            onTimeframeChange({ path, value }) {
                this.change({ path, value })

                if (path === 'searchByTimeWindow' && value === 'custom') {
                    this.showCustomCalendar = true
                    this.change({
                        path: 'customDate',
                        value: {
                            startDate: dayjs().startOf('day').toDate(),
                            endDate: null,
                        },
                    })
                } else if (path === 'searchByTimeWindow') {
                    this.showCustomCalendar = false
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/referrals/_app-direct-referral';
    .timeslot-tabs {
        margin-left: 40px;
        margin-right: 40px;
    }
    .calendar-area {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    header {
        text-align: center;

        p {
            font-size: 18px;
            color: #666666;
            word-spacing: 0.01em;
        }

        h4 {
            font-family: Poppins;
            font-size: 22px;
            text-align: center;
            margin-bottom: 1rem;
            color: #333333;
        }
    }

    main {
        p {
            font-size: 16px;
            text-align: center;
            color: #333333;
        }

        .contact_details {
            margin-top: 2rem;
            margin-bottom: 3rem;
        }
    }

    footer {
        text-align: center;

        h5 {
            font-size: 21px;
            margin: 1.5rem 0 2rem 0;
            font-weight: 500;
            color: #333333;
        }
    }

    hr {
        box-sizing: border-box;
        height: 0.1%;
        width: 98%;
        margin-left: 1%;
        border: 1px solid #d5d5d5;
    }
</style>
